import TransmissionList, {
  TransmissionListDetails,
  TransmissionListListItem,
} from '@/models/transmissionList'
import {
  TransmissionBatchDetails,
  PaginatedTransmissionBatchItems,
  TransmissionBatchItem,
  TransmissionBatchMessage,
  TransmissionBatchProgress,
  TransmissionBatchItemStatus,
  TransmissionListBatchStatus,
  TransmissionOperationalSnapshot,
} from '@/models/transmissionListBatch'
import { UTILS, TRANSMISSION_LIST_BATCH_STATUS } from '@/shared/constants'

export interface TransmissionBatchViewModel extends TransmissionBatchDetails {
  messages: TransmissionBatchMessage[]
  hasMessages: boolean
  isRunning: boolean
  isPaused: boolean
  isFinished: boolean
  showProgressBar: boolean
  showQueueWarning: boolean
  showResumeAction: boolean
  showPauseAction: boolean
  showPromoteAction: boolean
  showCancelAction: boolean
  canEdit: boolean
  canStart: boolean
  needsQueueCheck: boolean
  needsProviderQueueCheck: boolean
  isOperationallyBlocked: boolean
}

export interface TransmissionListDetailsViewModel extends TransmissionListDetails {
  activeBatch: TransmissionBatchViewModel | null
  batches: TransmissionBatchViewModel[]
}

const VALID_TRANSMISSION_BATCH_STATUSES: TransmissionListBatchStatus[] = [
  'pending',
  'waiting_slot',
  'in_progress',
  'paused',
  'paused_provider_block',
  'cancel_requested',
  'completed',
  'failed',
  'canceled',
]

const VALID_TRANSMISSION_BATCH_ITEM_STATUSES: TransmissionBatchItemStatus[] = [
  'pending',
  'processing',
  'sent_to_provider',
  'failed',
  'canceled',
  'skipped',
]

function normalizeTransmissionBatchStatus(
  status?: string
): TransmissionListBatchStatus {
  if (
    status &&
    VALID_TRANSMISSION_BATCH_STATUSES.includes(
      status as TransmissionListBatchStatus
    )
  ) {
    return status as TransmissionListBatchStatus
  }

  return TRANSMISSION_LIST_BATCH_STATUS.PENDING as TransmissionListBatchStatus
}

function normalizeTransmissionBatchItemStatus(
  status?: string
): TransmissionBatchItemStatus {
  if (
    status &&
    VALID_TRANSMISSION_BATCH_ITEM_STATUSES.includes(
      status as TransmissionBatchItemStatus
    )
  ) {
    return status as TransmissionBatchItemStatus
  }

  return 'pending'
}

function normalizeOperationalSnapshot(
  snapshot?: Partial<TransmissionOperationalSnapshot> | null
): TransmissionOperationalSnapshot {
  return {
    isTransmissionBlocked: snapshot?.isTransmissionBlocked ?? false,
    blockReason: snapshot?.blockReason ?? null,
    blockedAt: snapshot?.blockedAt ?? null,
    providerQueueCount: snapshot?.providerQueueCount ?? 0,
    lastProviderQueueCheckAt: snapshot?.lastProviderQueueCheckAt ?? null,
  }
}

export function mapTransmissionBatchProgress(
  batch?: Partial<TransmissionBatchProgress> | null
): TransmissionBatchProgress | null {
  if (!batch) {
    return null
  }

  const totalJobs = batch.totalJobs ?? 0
  const completedJobs = batch.completedJobs ?? 0
  const failedJobs = batch.failedJobs ?? 0
  const processedJobs = batch.processedJobs ?? completedJobs + failedJobs
  const remainingJobs =
    batch.remainingJobs ?? Math.max(0, totalJobs - processedJobs)
  const progressPercent =
    batch.progressPercent ??
    (totalJobs > 0
      ? Math.min(100, Math.round((processedJobs / totalJobs) * 100))
      : 0)
  const operationalSnapshot = normalizeOperationalSnapshot(batch)

  return {
    id: String(batch.id ?? UTILS.BLANK),
    status: normalizeTransmissionBatchStatus(batch.status),
    totalJobs,
    completedJobs,
    failedJobs,
    processedJobs,
    remainingJobs,
    progressPercent,
    estimatedTotalProcessingMs: batch.estimatedTotalProcessingMs ?? null,
    estimatedTotalProcessingMinutes:
      batch.estimatedTotalProcessingMinutes ?? null,
    estimatedRemainingMs: batch.estimatedRemainingMs ?? null,
    estimatedRemainingMinutes: batch.estimatedRemainingMinutes ?? null,
    estimatedCompletionAt: batch.estimatedCompletionAt ?? null,
    startedAt: batch.startedAt ?? null,
    finishedAt: batch.finishedAt ?? null,
    pausedAt: batch.pausedAt ?? null,
    pauseReason: batch.pauseReason ?? null,
    ...operationalSnapshot,
    canResume: batch.canResume ?? false,
    canPause: batch.canPause ?? false,
    canPromote: batch.canPromote ?? false,
    actionBlockedReason: batch.actionBlockedReason ?? null,
    failureReason: batch.failureReason ?? null,
    needsProviderQueueClear:
      batch.needsProviderQueueClear ??
      operationalSnapshot.providerQueueCount > 0,
  }
}

export function mapTransmissionBatchDetails(
  batch?: Partial<TransmissionBatchDetails> | null
): TransmissionBatchDetails | null {
  const progress = mapTransmissionBatchProgress(batch)

  if (!progress) {
    return null
  }

  return {
    ...progress,
    companyId: batch?.companyId ?? 0,
    transmissionListId: batch?.transmissionListId ?? 0,
    whatsappIntegrationId: batch?.whatsappIntegrationId ?? 0,
    scheduledMessageId: batch?.scheduledMessageId ?? null,
    createdAt: batch?.createdAt ?? UTILS.BLANK,
    updatedAt: batch?.updatedAt ?? UTILS.BLANK,
    canceledAt: batch?.canceledAt ?? null,
    eligibleContactIds: batch?.eligibleContactIds ?? [],
    blockedAt: batch?.blockedAt ?? progress.blockedAt ?? null,
    canCancel:
      batch?.canCancel ??
      [
        TRANSMISSION_LIST_BATCH_STATUS.PENDING,
        TRANSMISSION_LIST_BATCH_STATUS.WAITING_SLOT,
        TRANSMISSION_LIST_BATCH_STATUS.IN_PROGRESS,
        TRANSMISSION_LIST_BATCH_STATUS.PAUSED_PROVIDER_BLOCK,
        TRANSMISSION_LIST_BATCH_STATUS.PAUSED,
        TRANSMISSION_LIST_BATCH_STATUS.CANCEL_REQUESTED,
      ].includes(progress.status),
    messages: batch?.messages ?? [],
  }
}

export function mapTransmissionBatchItem(
  item?: Partial<TransmissionBatchItem> | null
): TransmissionBatchItem | null {
  if (!item) {
    return null
  }

  return {
    id: String(item.id ?? UTILS.BLANK),
    contactId: item.contactId ?? 0,
    contactName: item.contactName ?? UTILS.BLANK,
    cellPhone: item.cellPhone ?? UTILS.BLANK,
    status: normalizeTransmissionBatchItemStatus(item.status),
    orderIndex: item.orderIndex ?? 0,
    errorReason: item.errorReason ?? null,
    conversationId: item.conversationId ?? null,
    templateMessageIds: item.templateMessageIds ?? [],
    sentTemplateMessageIds: item.sentTemplateMessageIds ?? [],
    localMessageIds: item.localMessageIds ?? [],
    providerMessageIds: item.providerMessageIds ?? [],
    providerZaapIds: item.providerZaapIds ?? [],
    startedAt: item.startedAt ?? null,
    sentToProviderAt: item.sentToProviderAt ?? null,
    finishedAt: item.finishedAt ?? null,
    createdAt: item.createdAt ?? UTILS.BLANK,
    updatedAt: item.updatedAt ?? UTILS.BLANK,
  }
}

export function mapPaginatedTransmissionBatchItems(
  payload?: Partial<PaginatedTransmissionBatchItems> | null
): PaginatedTransmissionBatchItems {
  return {
    page: payload?.page ?? 1,
    pageSize: payload?.pageSize ?? 20,
    total: payload?.total ?? 0,
    items: (payload?.items ?? [])
      .map((item) => mapTransmissionBatchItem(item))
      .filter((item): item is TransmissionBatchItem => !!item),
  }
}

export function mapTransmissionBatchViewModel(
  batch: TransmissionBatchDetails
): TransmissionBatchViewModel {
  const messages = batch.messages ?? []
  const hasMessages = messages.length > 0
  const isRunning = [
    TRANSMISSION_LIST_BATCH_STATUS.WAITING_SLOT,
    TRANSMISSION_LIST_BATCH_STATUS.IN_PROGRESS,
  ].includes(batch.status)
  const isPaused = [
    TRANSMISSION_LIST_BATCH_STATUS.PAUSED,
    TRANSMISSION_LIST_BATCH_STATUS.PAUSED_PROVIDER_BLOCK,
  ].includes(batch.status)
  const isFinished = [
    TRANSMISSION_LIST_BATCH_STATUS.COMPLETED,
    TRANSMISSION_LIST_BATCH_STATUS.FAILED,
    TRANSMISSION_LIST_BATCH_STATUS.CANCELED,
  ].includes(batch.status)
  const showProgressBar = [
    TRANSMISSION_LIST_BATCH_STATUS.IN_PROGRESS,
    TRANSMISSION_LIST_BATCH_STATUS.WAITING_SLOT,
  ].includes(batch.status)
  const showQueueWarning =
    batch.isTransmissionBlocked || batch.needsProviderQueueClear
  const showResumeAction =
    batch.status === TRANSMISSION_LIST_BATCH_STATUS.PAUSED_PROVIDER_BLOCK ||
    batch.status === TRANSMISSION_LIST_BATCH_STATUS.PAUSED
  const showPauseAction = batch.canPause
  const showPromoteAction = batch.canPromote
  const showCancelAction = batch.canCancel
  const isOperationallyBlocked =
    batch.status === TRANSMISSION_LIST_BATCH_STATUS.PAUSED_PROVIDER_BLOCK ||
    batch.isTransmissionBlocked

  return {
    ...batch,
    messages,
    hasMessages,
    isRunning,
    isPaused,
    isFinished,
    showProgressBar,
    showQueueWarning,
    showResumeAction,
    showPauseAction,
    showPromoteAction,
    showCancelAction,
    canEdit: batch.status === TRANSMISSION_LIST_BATCH_STATUS.PENDING,
    canStart:
      batch.status === TRANSMISSION_LIST_BATCH_STATUS.PENDING && hasMessages,
    needsQueueCheck: isOperationallyBlocked,
    needsProviderQueueCheck: isOperationallyBlocked,
    isOperationallyBlocked,
  }
}

export function sortTransmissionListBatches(
  batches: TransmissionBatchDetails[] | undefined
) {
  return [...(batches ?? [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )
}

export function mapTransmissionListListItem(
  transmissionList: Partial<TransmissionList>
): TransmissionListListItem {
  return {
    id: transmissionList.id ?? 0,
    companyId: transmissionList.companyId ?? 0,
    name: transmissionList.name ?? UTILS.BLANK,
    isDefault: transmissionList.isDefault ?? false,
    lastSentAt: transmissionList.lastSentAt ?? null,
    contactsCount:
      transmissionList.contactsCount ?? transmissionList.contacts?.length ?? 0,
    nicheIds: transmissionList.nicheIds ?? [],
    taxRegimes: transmissionList.taxRegimes ?? [],
    latestBatch: mapTransmissionBatchProgress(transmissionList.latestBatch),
    operationalSnapshot: normalizeOperationalSnapshot(
      transmissionList.operationalSnapshot
    ),
  }
}

export function mapTransmissionListDetails(
  transmissionList: Partial<TransmissionListDetails>
): TransmissionListDetailsViewModel {
  const mappedBatches = sortTransmissionListBatches(
    (transmissionList.batches ?? [])
      .map((batch) => mapTransmissionBatchDetails(batch))
      .filter((batch): batch is TransmissionBatchDetails => !!batch)
  ).map(mapTransmissionBatchViewModel)

  const activeBatch =
    mapTransmissionBatchDetails(transmissionList.activeBatch) ??
    mappedBatches[mappedBatches.length - 1] ??
    null

  return {
    id: transmissionList.id ?? 0,
    companyId: transmissionList.companyId ?? 0,
    name: transmissionList.name ?? UTILS.BLANK,
    isDefault: transmissionList.isDefault ?? false,
    lastSentAt: transmissionList.lastSentAt ?? null,
    contactsCount:
      transmissionList.contactsCount ?? transmissionList.contacts?.length ?? 0,
    latestBatch: mapTransmissionBatchProgress(transmissionList.latestBatch),
    operationalSnapshot: normalizeOperationalSnapshot(
      transmissionList.operationalSnapshot
    ),
    taxRegimes: transmissionList.taxRegimes ?? [],
    contacts: transmissionList.contacts ?? [],
    batches: mappedBatches,
    activeBatch: activeBatch
      ? mapTransmissionBatchViewModel(activeBatch)
      : null,
  }
}

export function getEditableTransmissionBatch(
  batches: TransmissionBatchViewModel[]
) {
  return [...batches]
    .reverse()
    .find((batch) => batch.status === TRANSMISSION_LIST_BATCH_STATUS.PENDING)
}

export function hasRunningTransmissionBatch(
  batches: Array<Pick<TransmissionBatchProgress, 'status'>>
) {
  return batches.some((batch) =>
    [
      TRANSMISSION_LIST_BATCH_STATUS.WAITING_SLOT,
      TRANSMISSION_LIST_BATCH_STATUS.IN_PROGRESS,
      TRANSMISSION_LIST_BATCH_STATUS.PAUSED_PROVIDER_BLOCK,
    ].includes(batch.status)
  )
}

export function applyBatchProgressToTransmissionList(
  transmissionList: TransmissionListDetailsViewModel,
  batchProgress: TransmissionBatchProgress
): TransmissionListDetailsViewModel {
  const nextBatches = transmissionList.batches.map((batch) => {
    if (batch.id !== batchProgress.id) {
      return batch
    }

    return mapTransmissionBatchViewModel({
      ...batch,
      ...batchProgress,
      blockedAt: batchProgress.blockedAt ?? batch.blockedAt ?? null,
      canCancel: batch.canCancel,
      messages: batch.messages,
      createdAt: batch.createdAt,
      updatedAt: batch.updatedAt,
      canceledAt: batch.canceledAt,
      eligibleContactIds: batch.eligibleContactIds,
      companyId: batch.companyId,
      transmissionListId: batch.transmissionListId,
      whatsappIntegrationId: batch.whatsappIntegrationId,
      scheduledMessageId: batch.scheduledMessageId,
    })
  })

  const nextActiveBatch =
    nextBatches.find((batch) => batch.id === batchProgress.id) ?? null

  return {
    ...transmissionList,
    batches: nextBatches,
    activeBatch: nextActiveBatch,
    operationalSnapshot: {
      isTransmissionBlocked: batchProgress.isTransmissionBlocked,
      blockReason: batchProgress.blockReason,
      blockedAt: batchProgress.blockedAt,
      providerQueueCount: batchProgress.providerQueueCount,
      lastProviderQueueCheckAt: batchProgress.lastProviderQueueCheckAt,
    },
  }
}
